import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { FuseAlertComponent } from '@fuse/components/alert';
import { TranslocoModule } from '@ngneat/transloco';

/**
 * Paso «guardar los códigos de recuperación»: tarjeta con los códigos, copiar,
 * descargar, la advertencia y el checkbox de confirmación. PRESENTACIONAL puro
 * — el host decide qué hace con `acknowledged` (habilitar su botón de salida).
 *
 * Es el único paso irrepetible del flujo: si se cierra sin guardar los códigos
 * hay que reconfigurar el MFA desde cero. Por eso copiar y descargar se
 * resuelven acá, en el front (`navigator.clipboard` + `Blob`), sin endpoint
 * nuevo, y el nombre del archivo NO lleva datos del usuario.
 */
@Component({
  selector: 'app-mfa-recovery-codes',
  templateUrl: './mfa-recovery-codes.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatCheckboxModule,
    MatIconModule,
    FuseAlertComponent,
    TranslocoModule,
  ],
})
export class MfaRecoveryCodesComponent {
  @Input({ required: true }) codes: string[] = [];

  /** Estado del checkbox de confirmación («ya los guardé»). */
  @Output() readonly acknowledged = new EventEmitter<boolean>();

  readonly copied = signal(false);
  /** La escritura al portapapeles falló: se avisa y queda «Descargar». */
  readonly copyFailed = signal(false);

  /**
   * `navigator.clipboard` no existe en contextos inseguros: si no está, se
   * oculta el botón de copiar y queda sólo el de descargar.
   */
  readonly canCopy =
    typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText;

  /** Numeración de la grilla: 01..10. */
  pad(index: number): string {
    return String(index + 1).padStart(2, '0');
  }

  copyCodes(): void {
    if (!this.canCopy) {
      return;
    }

    this.copyFailed.set(false);
    // «Copiado» SÓLO si resolvió: con `void` el rótulo mentía ante un rechazo,
    // en el paso irrepetible. El rechazo no se loguea (los códigos, jamás).
    navigator.clipboard.writeText(this.codes.join('\n')).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    }, () => this.copyFailed.set(true));
  }

  downloadCodes(): void {
    const blob = new Blob([this.codes.join('\n') + '\n'], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    // Sin datos del usuario en el nombre del archivo.
    anchor.download = 'pvp-codigos-de-recuperacion.txt';
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
